/**
 * What the launcher needs to know before anyone clicks it: whether to render at
 * all, and what this organization calls its assistant.
 *
 * Separate from the panel's own capability probe on purpose. The panel asks
 * when it OPENS; the launcher has to decide before that, and it has to render
 * the org's name in its tooltip and aria-label rather than the word "Copilot"
 * — a control whose label disagrees with the panel it opens is worse than a
 * generic one.
 *
 * It self-hides rather than showing a control that answers with a shrug. The
 * backend decides: `enabled` is false for a Triveny tenant, a member-portal
 * account, an org outside the pilot, or a caller whose role and capabilities
 * leave the tool catalogue empty.
 */
import { useEffect, useState } from 'react';
import { fetchCopilotCapabilities } from '@/services/copilotStream';

/** The platform default. Mirrors DEFAULT_ASSISTANT_NAME on the backend. */
export const DEFAULT_ASSISTANT_NAME = 'Wren';

export interface CopilotLauncherState {
  /** Render the launcher? False until the probe answers, so it never flickers
   *  in and out for someone who cannot use it. */
  enabled: boolean;
  /** What to call it. Never empty — the default stands in. */
  name: string;
}

export function useCopilotLauncher(isAuthenticated: boolean): CopilotLauncherState {
  const [state, setState] = useState<CopilotLauncherState>({
    enabled: false,
    name: DEFAULT_ASSISTANT_NAME,
  });

  useEffect(() => {
    if (!isAuthenticated) {
      setState({ enabled: false, name: DEFAULT_ASSISTANT_NAME });
      return;
    }
    const controller = new AbortController();
    let live = true;
    fetchCopilotCapabilities(controller.signal).then((caps) => {
      if (!live) return;
      setState({
        enabled: Boolean(caps?.enabled),
        // An org that has not renamed it, an older backend that does not send
        // the field, and an unreachable probe all land on the same default.
        name: (caps?.assistant_name || '').trim() || DEFAULT_ASSISTANT_NAME,
      });
    });
    return () => {
      live = false;
      controller.abort();
    };
  }, [isAuthenticated]);

  return state;
}

export default useCopilotLauncher;
